import React from 'react';
import { motion } from 'framer-motion';
import type { RegisterVisual } from './registerVisuals';
import { RegisterCardCarousel } from './RegisterCardCarousel';

/**
 * Layout condiviso del flusso di registrazione (struttura B, stile Attio):
 * - colonna sinistra su bianco con logo in alto e form centrato
 * - pannello destro #F7F6F3 (grigio sidebar) con le card prodotto del sito
 *   in crossfade (RegisterCardCarousel), oppure un nodo custom
 * - sotto lg il pannello destro sparisce (solo colonna form)
 * Le pagine passano SOLO il contenuto del form; niente notion-register-card,
 * niente sfondo scuro notion-register-bg.
 */
export interface RegisterSplitLayoutProps {
  children: React.ReactNode;
  /** Card di partenza del carosello oppure nodo custom (es. SidebarPreview nello step moduli). */
  visual?: RegisterVisual | React.ReactNode;
  /** Progress "Passo X di Y" sopra il form (omesso se step assente). */
  step?: number;
  totalSteps?: number;
  contentClassName?: string;
  /** Lingua della copy delle card (solo l'entry ha il toggle IT/EN). */
  language?: 'it' | 'en';
}

function isRegisterVisual(v: RegisterVisual | React.ReactNode): v is RegisterVisual {
  return !!v && typeof v === 'object' && 'startCard' in (v as Record<string, unknown>);
}

export function RegisterSplitLayout({
  children,
  visual,
  step,
  totalSteps = 9,
  contentClassName = '',
  language = 'it',
}: RegisterSplitLayoutProps) {
  return (
    <div className="min-h-screen flex bg-white">
      {/* Colonna form (full-width quando non c'è il visual, es. schermate di transizione) */}
      <div className={visual != null ? 'w-full lg:w-[52%] xl:w-[48%] flex flex-col min-h-screen' : 'w-full flex flex-col min-h-screen'}>
        <div className="px-6 sm:px-10 pt-8 flex items-center justify-between">
          <img src="/logo_colorato.png" alt="Flusia" className="h-8 w-auto object-contain" />
          {typeof step === 'number' && (
            <span className="text-xs text-[#787774]">
              Passo {step} di {totalSteps}
            </span>
          )}
        </div>
        <div className="flex-1 flex items-center justify-center px-6 sm:px-10 py-8">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className={`w-full max-w-md ${contentClassName}`}
          >
            {children}
          </motion.div>
        </div>
      </div>

      {/* Pannello visual (solo desktop, non renderizzato se visual assente) */}
      {visual != null && (
        <div className="hidden lg:block flex-1 bg-[#F7F6F3] border-l border-[rgba(55,53,47,0.09)] overflow-hidden relative">
          {isRegisterVisual(visual) ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="absolute inset-0"
            >
              <RegisterCardCarousel startCard={visual.startCard} language={language} />
            </motion.div>
          ) : (
            <div className="absolute inset-0 flex items-center justify-end">
              <motion.div
                initial={{ opacity: 0, x: 24 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="w-[88%] max-h-[80vh]"
              >
                {visual}
              </motion.div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
